<template>
    <v-dialog :model-value="dialogWindowVisibility" width="fit-content" @after-leave="closeWindow">
        <v-card>
            <v-card-title class="text-body-1 ma-1 mr-1">
                <div v-text="$t('transfers.specify_exact_address_window_title')" />
                <v-icon class="v-card-close" icon="mdi-close" @click="closeWindow" />
            </v-card-title>
            <div class="map-wrapper">
                <div ref="map" class="map-element" />
                <address-map-location-filter
                    v-if="google && city"
                    v-model="point"
                    class="search-input"
                    :google="google"
                    :city="city"
                />
            </div>
            <v-btn color="primary" class="ma-3 ml-auto" @click="confirmPoint">{{ $t('confirm') }}</v-btn>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, VModel, Watch} from 'vue-facing-decorator'
    import gmapsInit from '~/utils/gmaps'
    import {HotelMapMixin} from '~src/components/hotels/mixins/hotelMapMixin.src'
    import AddressMapLocationFilter from '@/components/transfers/snippets/AddressMapLocationFilter.vue'

    @Component({components: {AddressMapLocationFilter}})
    export class SpecifyExactAddressModal extends HotelMapMixin {
        @VModel({default: null}) model

        dialogWindowVisibility = true
        google = null
        map = null
        marker = null
        geocoder = null
        city = null
        point = null
        pointLocation = null
        postalCode = null

        async mounted() {
            await this.initMap()
            this.geocoder = new this.google.maps.Geocoder()

            if (this.model.type === 'position') {
                this.point = {...this.model, description: this.model.address}
                this.city = this.model.city
            } else {
                this.city = this.model
                await this.updatePointLocation()
            }

            this.updateMarker()
        }

        async initMap() {
            try {
                this.google = await gmapsInit()
                this.map = new this.google.maps.Map(this.$refs.map, {
                    disableDefaultUI: true,
                    gestureHandling: 'greedy',
                    mapTypeControl: true,
                    mapTypeControlOptions: {
                        style: this.google.maps.MapTypeControlStyle.DEFAULT,
                        position: this.google.maps.ControlPosition.LEFT_BOTTOM,
                    },
                    zoomControl: true,
                    fullscreenControl: !this.isMobile,
                    zoom: 8,
                    restriction: {
                        latLngBounds: {north: 80, south: -70, west: -180, east: 180},
                        strictBounds: true,
                    },
                })
            } catch (error) {
                console.error(error)
                this.closeWindow()
            }
        }

        @Watch('point')
        async changePoint() {
            if (!this.map || !this.city.latitude || !this.city.longitude) return

            await this.updatePointLocation()
            this.updateMarker()
        }

        updateMarker() {
            if (this.marker) {
                toRaw(this.marker).setMap(null)
                this.marker = null
            }

            if (this.pointLocation) {
                this.marker = new this.google.maps.Marker({
                    position: this.pointLocation,
                    map: this.map,
                    icon: this.createMarkerIcon(this.point),
                })

                this.map.setZoom(18)
                this.map.setCenter(this.pointLocation)
            } else if (this.city && this.city.latitude && this.city.longitude) {
                this.map.setZoom(8)
                this.map.setCenter({lat: this.city.latitude, lng: this.city.longitude})
            }
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        closeWindow() {
            this.$emit('closed')
            this.dialogWindowVisibility = false
        }

        confirmPoint() {
            if (this.point && !isEqual(this.point, this.model)) {
                const {description, address, place_id} = this.point
                const pointAddress = description || address
                const {lat, lng} = this.pointLocation

                this.model = {
                    type: 'position',
                    address: pointAddress,
                    latitude: lat,
                    longitude: lng,
                    city: clone(this.city),
                    title: pointAddress,
                    placeId: place_id,
                    postalCode: this.postalCode,
                }
            }

            this.closeWindow()
        }

        async updatePointLocation() {
            if (!this.point) {
                this.pointLocation = null
            } else {
                if (this.point.latitude && this.point.longitude) {
                    this.pointLocation = {lat: this.point.latitude, lng: this.point.longitude}
                } else {
                    const {results} = await this.geocoder.geocode({placeId: this.point.place_id})
                    this.pointLocation = results[0]?.geometry.location.toJSON()
                    this.postalCode = results[0]?.address_components.find(i =>
                        i.types.includes('postal_code')
                    )?.long_name
                }
            }
        }
    }

    export default SpecifyExactAddressModal
</script>

<style scoped lang="scss">
    .map-wrapper {
        position: relative;
        width: 932px;
        max-width: 100%;
        height: 440px;
        max-height: 100%;

        .map-element {
            width: 100%;
            height: 100%;
        }

        .search-input {
            position: absolute;
            top: 0;
            left: 0;
        }
    }
</style>
